/**
 * CURRENCY-1 — one currency formatter for the analyst workspace.
 *
 * Before this, every panel built its own `Intl.NumberFormat('en-US', {currency:
 * 'USD', maximumFractionDigits: 0})` inline. Two consequences the CPA sees:
 * the Summary panel showed "$2,150,000" while the grid that produced it showed
 * a bare "2150000", and cents were rounded away everywhere — so a figure typed
 * as 2,150,000.40 read back as $2,150,000 and looked like a typo.
 *
 * Currency is a per-deal field. It is defaulted from the *browser locale*
 * (`navigator.language`), never from IP geolocation: the CPA's browser language
 * is a setting they control, an IP is not, and a US firm working a UK target
 * over a VPN would otherwise get silently mislabelled figures.
 */

/** Currencies conventionally written without minor units. */
const ZERO_DECIMAL = new Set(['JPY', 'KRW', 'VND', 'CLP', 'ISK', 'HUF'])

/**
 * Region -> ISO-4217, for the currencies a mid-market QoE engagement plausibly
 * settles in. Anything unlisted falls back to USD, which is also the column
 * default on the deals table.
 */
const REGION_CURRENCY: Record<string, string> = {
  US: 'USD', PR: 'USD', GU: 'USD', VI: 'USD', EC: 'USD',
  GB: 'GBP', IE: 'EUR', DE: 'EUR', FR: 'EUR', ES: 'EUR', IT: 'EUR',
  NL: 'EUR', BE: 'EUR', AT: 'EUR', PT: 'EUR', FI: 'EUR', GR: 'EUR',
  LU: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR', LT: 'EUR',
  CY: 'EUR', MT: 'EUR', HR: 'EUR',
  CA: 'CAD', AU: 'AUD', NZ: 'NZD', CH: 'CHF',
  SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF',
  IN: 'INR', JP: 'JPY', CN: 'CNY', HK: 'HKD', SG: 'SGD', KR: 'KRW',
  MY: 'MYR', TH: 'THB', PH: 'PHP', ID: 'IDR',
  AE: 'AED', SA: 'SAR', IL: 'ILS', ZA: 'ZAR', NG: 'NGN', KE: 'KES',
  BR: 'BRL', MX: 'MXN', AR: 'ARS', CL: 'CLP', CO: 'COP',
}

export const DEFAULT_CURRENCY = 'USD'

/** The currency picker offered on a deal. Ordered by how often we see them. */
export const CURRENCY_OPTIONS: { code: string; label: string }[] = [
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'NZD', label: 'NZD — New Zealand Dollar' },
  { code: 'CHF', label: 'CHF — Swiss Franc' },
  { code: 'SEK', label: 'SEK — Swedish Krona' },
  { code: 'NOK', label: 'NOK — Norwegian Krone' },
  { code: 'DKK', label: 'DKK — Danish Krone' },
  { code: 'INR', label: 'INR — Indian Rupee' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'AED', label: 'AED — UAE Dirham' },
  { code: 'ZAR', label: 'ZAR — South African Rand' },
  { code: 'BRL', label: 'BRL — Brazilian Real' },
  { code: 'MXN', label: 'MXN — Mexican Peso' },
]

/**
 * Best-guess currency for a brand-new deal, taken from the browser's own
 * language/region setting. Never a network lookup, never IP-derived.
 */
export function localeDefaultCurrency(): string {
  if (typeof navigator === 'undefined') return DEFAULT_CURRENCY
  const tags = [navigator.language, ...(navigator.languages ?? [])].filter(Boolean)
  for (const tag of tags) {
    let region: string | undefined
    try {
      region = new Intl.Locale(tag).maximize().region
    } catch {
      region = tag.split('-')[1]?.toUpperCase()
    }
    const code = region ? REGION_CURRENCY[region] : undefined
    if (code) return code
  }
  return DEFAULT_CURRENCY
}

export function currencyDecimals(currency: string): number {
  return ZERO_DECIMAL.has(currency.toUpperCase()) ? 0 : 2
}

/**
 * "$2,150,000.00". Cents are shown — a Quality-of-Earnings figure that has
 * been rounded on screen cannot be tied back to the trial balance.
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string = DEFAULT_CURRENCY,
  fallback = '—',
): string {
  if (value == null || Number.isNaN(value)) return fallback
  const digits = currencyDecimals(currency)
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value)
  } catch {
    // Unknown/malformed ISO code — still show the number rather than an error.
    return `${currency} ${value.toFixed(digits)}`
  }
}

/** Currency symbol on its own, for input adornments and column headers. */
export function currencySymbol(currency: string = DEFAULT_CURRENCY): string {
  try {
    return (
      new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
        .formatToParts(0)
        .find(p => p.type === 'currency')?.value ?? currency
    )
  } catch {
    return currency
  }
}

// ── Editable numeric fields ─────────────────────────────────────────────────
//
// The typed representation is deliberately fixed: comma thousands separator,
// dot decimal separator, regardless of the deal's currency. Grouping in the
// *viewer's* locale would make the round-trip ambiguous — in de-DE, Intl emits
// "1.234" for one thousand two hundred thirty-four, which re-parses as 1.234.
// The UI language here is English; only the displayed currency varies.

/**
 * Reformat a partially-typed value for display. Keeps a trailing "." and
 * trailing zeros so "1200." and "1200.50" are both typeable without the
 * cursor fighting the formatter.
 */
export function formatNumericInput(raw: string, maxDecimals = 2): string {
  const negative = raw.trimStart().startsWith('-')
  const cleaned = raw.replace(/[^\d.]/g, '')
  const dot = cleaned.indexOf('.')
  const intDigits = (dot === -1 ? cleaned : cleaned.slice(0, dot)).replace(/^0+(?=\d)/, '')
  const decDigits = dot === -1 ? '' : cleaned.slice(dot + 1).replace(/\./g, '').slice(0, maxDecimals)
  const grouped = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const body = `${grouped}${dot === -1 ? '' : `.${decDigits}`}`
  if (body === '') return negative ? '-' : ''
  return `${negative ? '-' : ''}${body}`
}

/** Display string -> the plain number we store and send. '' means "not entered". */
export function parseNumericInput(raw: string): number | '' {
  const cleaned = raw.replace(/,/g, '').replace(/[^\d.-]/g, '')
  if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') return ''
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : ''
}

/** Stored number -> the display string shown when the field is not being typed in. */
export function numericInputValue(value: number | '' | null | undefined): string {
  if (value === '' || value == null || Number.isNaN(value)) return ''
  return formatNumericInput(String(value))
}
